import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useCart } from '@/hooks/useCart';
import { useCatalog } from '@/hooks/useCatalog';

/**
 * useReorder — rebuild a previous order into the cart in one tap.
 *
 * The canonical line-item shape lives in `orders.order_lines`, one row per
 * line with a direct `catalog_item_id` foreign key to `catalog.id` (the same
 * key the cart is keyed by — see useCart). So the mapping is a straight
 * id lookup against the *current* catalog: no SKU/name fuzzy-matching needed.
 *
 * Items whose catalog row has since rolled off (deactivated, no longer
 * orderable, or deleted) simply won't be in `useCatalog`'s result, so they're
 * skipped gracefully and reported in the summary. We capture their name from
 * the order_lines snapshot (`item_name`) so the UI can tell the customer what
 * couldn't be re-added.
 *
 * Adds use `adjustQty` (delta += ordered qty) so reordering APPENDS to the
 * existing cart rather than wiping it — matching the rest of the app's
 * cart-merge behavior. The active customer / location context is owned by the
 * cart + submit flow, so this hook stays context-agnostic.
 *
 * RLS scopes the order_lines read to the active customer automatically.
 */

export interface ReorderSummary {
  added: number;
  skipped: number;
  skippedNames: string[];
}

interface OrderLineRow {
  catalog_item_id: string | null;
  quantity: number | string | null;
  item_name: string | null;
}

export function useReorder() {
  const { adjustQty } = useCart();
  const { data: catalog = [] } = useCatalog();

  /**
   * Rebuild a set of order lines (already fetched) into the cart.
   * Returns a summary of what was added vs. skipped.
   */
  const reorderLines = useCallback(
    (lines: OrderLineRow[]): ReorderSummary => {
      // Only items currently in the catalog (useCatalog already filters to
      // active + orderable) are eligible — everything else is "unavailable".
      const liveIds = new Set(catalog.map((c) => c.id));

      // Accumulate per catalog id first so a line that appears twice in an
      // order (rare, but possible) adds its combined qty in one shot.
      const toAdd = new Map<string, number>();
      const skippedNames: string[] = [];

      for (const line of lines) {
        const id = line.catalog_item_id;
        const qty = Math.max(0, Math.floor(Number(line.quantity ?? 0)));
        if (qty <= 0) continue;

        if (!id || !liveIds.has(id)) {
          // Unavailable / rolled-off item — report by its snapshot name.
          skippedNames.push(line.item_name?.trim() || 'Unknown item');
          continue;
        }
        toAdd.set(id, (toAdd.get(id) ?? 0) + qty);
      }

      for (const [id, qty] of toAdd) {
        adjustQty(id, qty);
      }

      return {
        added: toAdd.size,
        skipped: skippedNames.length,
        skippedNames,
      };
    },
    [adjustQty, catalog],
  );

  /**
   * Fetch an order's lines by id, then rebuild them into the cart.
   * Returns null if the order has no readable lines.
   */
  const reorderById = useCallback(
    async (orderId: string): Promise<ReorderSummary | null> => {
      const { data, error } = await supabase
        .schema('orders')
        .from('order_lines')
        .select('catalog_item_id, quantity, item_name')
        .eq('order_id', orderId)
        .order('line_number');

      if (error || !data || data.length === 0) return null;
      return reorderLines(data as OrderLineRow[]);
    },
    [reorderLines],
  );

  return { reorderById, reorderLines };
}
